import { useMemo } from 'react';
import {
  useModelCatalog,
  type ModelCapability,
  type ModelCatalogResponse,
  type ModelOption,
  type ModelProviderKey,
} from '@easyflow/ui-frontend';
import { apiClient } from '@/lib/api';
import type { LLMProviderExtended, ModelCapabilities } from '@/constants/ai-providers';

/**
 * Task #1355 — Hook FE adapter che migra i 6 menu legacy `useLLMModels` al
 * catalog dinamico (`useModelCatalog` da `@easyflow/ui-frontend`, endpoint
 * `/api/workflow-nodes/options/models`). Espone una shape compatibile col
 * vecchio `LLMModelEntry` (id/displayName/pricing/...) per minimizzare il
 * churn nei consumer, mantenendo tutta la logica di fetching/cache nel
 * catalog hook condiviso.
 *
 * Provider mapping legacy → catalog:
 *   openai → openai (chat)
 *   claude → claude (chat)
 *   gemini → google (chat)
 *   openai-realtime → openai (realtime_voice)
 *   gemini-voice    → google (realtime_voice)
 *
 * Capability override: passare `capability: 'vision'` filtra `features∋vision`
 * server-side (vedi mapping in workflow-node-options.routes.ts).
 */

export interface ChatModelEntry {
  id: string;
  displayName: string;
  name: string;
  description: string;
  contextWindow: number;
  isRecommended: boolean;
  status: 'available' | 'deprecated' | 'preview';
  pricing: {
    input: number;
    output: number;
    cached_input?: number;
    currency?: string;
    unit?: string;
  } | null;
  pricing_unknown: boolean;
  capabilities?: ModelCapabilities;
}

export interface ChatModelOptionsPayload {
  provider: string;
  models: ChatModelEntry[];
  recommended?: string;
  source: 'live' | 'cache' | 'fallback';
  syncDegraded?: boolean;
  lastSyncOkAt?: number | null;
  lastSyncFailedAt?: number | null;
}

interface ProviderMap {
  catalog: ModelProviderKey;
  capability: ModelCapability;
}

const PROVIDER_MAP: Record<string, ProviderMap> = {
  openai: { catalog: 'openai', capability: 'chat' },
  claude: { catalog: 'claude', capability: 'chat' },
  gemini: { catalog: 'google', capability: 'chat' },
  'openai-realtime': { catalog: 'openai-realtime', capability: 'realtime_voice' },
  'gemini-voice':    { catalog: 'gemini-voice',    capability: 'realtime_voice' },
  // Task #1984 round B — provider 'custom' bypassa il catalog globale: i
  // modelli vengono enumerati direttamente da `/custom-ai/endpoints/:id/models`
  // dal `LLMProviderConfig` (branch dedicato). Questa entry esiste solo per
  // far passare il guard `!!map` quando il provider è 'custom' senza
  // generare query verso `workflow-nodes/options/models`.
  custom:           { catalog: 'custom' as ModelProviderKey, capability: 'chat' },
};

function stripLabelTags(label: string): string {
  return label.replace(/\s*\((?:deprecato|preview)(?:,\s*(?:deprecato|preview))?\)\s*$/i, '');
}

function featuresToCapabilities(features: ReadonlyArray<string>): ModelCapabilities {
  return {
    tools: features.includes('tool_calling'),
    strict_schema: features.includes('json_mode'),
    vision: features.includes('vision'),
    embedding: features.includes('embedding'),
    rerank: features.includes('rerank'),
    realtime: features.includes('realtime'),
  };
}

function optionToEntry(opt: ModelOption): ChatModelEntry {
  const status: ChatModelEntry['status'] = opt.deprecated
    ? 'deprecated'
    : opt.badges.some((b) => b.kind === 'preview')
      ? 'preview'
      : 'available';
  const pricingMeta = opt.metadata.pricing;
  const pricing = pricingMeta
    ? {
        input: Number(pricingMeta.input_per_1k ?? 0),
        output: Number(pricingMeta.output_per_1k ?? 0),
        cached_input: pricingMeta.cached_input_per_1k,
        currency: pricingMeta.currency,
        unit: pricingMeta.unit,
      }
    : null;
  const cleanLabel = stripLabelTags(opt.label);
  return {
    id: opt.value,
    displayName: cleanLabel,
    name: cleanLabel,
    description: '',
    contextWindow: opt.metadata.context_window ?? 0,
    isRecommended: opt.recommended,
    status,
    pricing,
    pricing_unknown: !pricingMeta,
    capabilities: featuresToCapabilities(opt.metadata.features ?? []),
  };
}

export interface UseChatModelOptionsParams {
  capability?: 'vision';
  enabled?: boolean;
}

export interface UseChatModelOptionsResult {
  data: ChatModelOptionsPayload | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => Promise<void>;
}

export function useChatModelOptions(
  provider: LLMProviderExtended | undefined | null,
  params: UseChatModelOptionsParams = {},
): UseChatModelOptionsResult {
  const map = provider ? PROVIDER_MAP[provider] : undefined;
  const enabled = params.enabled !== false && !!map;
  const capability: ModelCapability = params.capability === 'vision' ? 'vision' : (map?.capability ?? 'chat');

  const q = useModelCatalog({
    capability,
    provider: map?.catalog,
    enabled,
  });

  const data: ChatModelOptionsPayload | undefined = useMemo(() => {
    if (!provider) return undefined;
    if (!q.data) {
      if (q.isError) {
        return { provider, models: [], source: 'fallback' };
      }
      return undefined;
    }
    const models = q.data.options.map(optionToEntry);
    const recommended = models.find((m) => m.isRecommended)?.id;
    return {
      provider,
      models,
      recommended,
      source: models.length > 0 ? 'live' : 'fallback',
    };
  }, [provider, q.data, q.isError]);

  return {
    data,
    isLoading: q.isLoading,
    isFetching: q.isLoading,
    isError: q.isError,
    refetch: q.refetch,
  };
}

export function chatModelOptionsQueryKey(
  provider: LLMProviderExtended,
  capability?: 'vision',
) {
  return ['chat-model-options', provider, capability ?? null] as const;
}

// Il payload `/workflow-nodes/options/models` è top-level (`ModelCatalogResponse`
// di ui-frontend, stessa shape della route): `source` è sempre 'catalog' e
// viene proiettata sui valori UI ('live'|'cache'|'fallback').
function normalizeSource(raw: string | undefined): ChatModelOptionsPayload['source'] {
  return raw === 'live' || raw === 'cache' || raw === 'fallback' ? raw : 'live';
}

export async function fetchChatModelOptions(
  provider: LLMProviderExtended,
  capability?: 'vision',
): Promise<ChatModelOptionsPayload> {
  const map = PROVIDER_MAP[provider];
  if (!map) {
    return { provider, models: [], source: 'fallback', syncDegraded: true };
  }
  const cap: ModelCapability = capability === 'vision' ? 'vision' : map.capability;
  const qs = new URLSearchParams({ capability: cap, provider: map.catalog });
  try {
    const body = await apiClient.getTopLevel<ModelCatalogResponse>(`/workflow-nodes/options/models?${qs.toString()}`);
    const models = (body.options ?? []).map(optionToEntry);
    const source = normalizeSource(body.source) || (models.length > 0 ? 'live' : 'fallback');
    return {
      provider,
      models,
      recommended: models.find((m) => m.isRecommended)?.id,
      source,
      syncDegraded: models.length === 0,
      lastSyncOkAt: null,
      lastSyncFailedAt: null,
    };
  } catch {
    return {
      provider,
      models: [],
      source: 'fallback',
      syncDegraded: true,
      lastSyncOkAt: null,
      lastSyncFailedAt: Date.now(),
    };
  }
}
